/**
 * src/lib/i18n/navigation.tsx
 *
 * next-intl locale-aware navigation. Pathnames DATA-DRIVEN:
 * siteSettings.routeSegments listesinden runtime'da next-intl pathnames objesi
 * uretilir. Phase β'da admin route segment'leri editlediginde sadece data degisir,
 * bu dosya dokunulmaz.
 *
 * Iki tabakali yaklasim:
 *   1. **Locale prefix** (always mode) — '/' kok her zaman '/<defaultLocale>'a redirect.
 *
 *   2. **Locale pathnames** — top-level static path'ler her locale'da farkli
 *      gozukur (TR: /hizmetler, EN: /services). Internal codebase tek key kullanir:
 *      `<Link href="/hizmetler">` -> middleware locale'a gore uretir:
 *        - TR session: /tr/hizmetler
 *        - EN session: /en/services
 *
 *      Dinamik [slug] route'lar (/hizmetler/[slug], /blog/[slug]) icin bizim
 *      Link wrapper'i string href'i regex-match ile template'e cevirir, sonra
 *      IntlLink'e {pathname, params} object form gonderir. Boylece
 *      `<Link href="/hizmetler/foo">` EN'de `/en/services/foo` uretilir.
 *
 * Kullanim:
 *   import { Link, useRouter } from '@/lib/i18n/navigation'
 *   <Link href="/hizmetler">Hizmetler</Link>             // -> /tr/hizmetler veya /en/services
 *   <Link href="/hizmetler/sizma-testi">Sizma Testi</Link> // -> /tr/hizmetler/sizma-testi veya /en/services/sizma-testi
 */

import { createNavigation } from 'next-intl/navigation'
import { defineRouting } from 'next-intl/routing'

import { siteSettings } from '@/data/site-settings'
import type { RouteSegment } from '@/data/types/site-settings'
import type { Locale } from '@/domain/value-objects/locale'

import { DEFAULT_LOCALE, ENABLED_LOCALES, LOCALE_PREFIX_MODE, SUPPORTED_LOCALES } from './config'

/**
 * routeSegments listesinden next-intl pathnames objesini build eder.
 *
 * Kurallar:
 *  - Tum locale'larda ayni path olan kayitlar tek string (orn. '/blog': '/blog').
 *  - Farkli path'li kayitlar locale map (orn. '/hizmetler': { tr: ..., en: ... }).
 *  - `pattern: 'dynamic-slug'` icin hem listing hem `/listing/[slug]` template
 *    eklenir.
 *
 * @param segments siteSettings.routeSegments listesi
 * @returns next-intl `pathnames` config'i (key = TR canonical path; value = string
 *   veya per-locale map)
 */
function buildPathnames(segments: readonly RouteSegment[]): Record<string, string | Record<string, string>> {
  const result: Record<string, string | Record<string, string>> = {}

  for (const seg of segments) {
    // TR (default) path'i canonical key; tum locale degerleri ayni mi kontrolu
    const trPath = seg.segments['tr']
    if (!trPath) {
      throw new Error(`RouteSegment "${seg.key}" missing 'tr' segment`)
    }

    // SUPPORTED_LOCALES içindeki her locale için segment garantile.
    // routeSegments'ta o locale eksikse default (TR) path'i kullan — yeni dil
    // aktif edildiğinde URL kırılmaz; admin sonradan /cms/diller'dan customize eder.
    const filledSegments: Record<string, string> = { ...seg.segments }
    for (const loc of SUPPORTED_LOCALES) {
      if (!filledSegments[loc]) {
        filledSegments[loc] = trPath
      }
    }

    const allSame = Object.values(filledSegments).every((p) => p === trPath)
    const value: string | Record<string, string> = allSame
      ? trPath
      : filledSegments

    result[trPath] = value

    // Dinamik listing icin [slug] template eklenir
    if (seg.pattern === 'dynamic-slug') {
      const dynamicKey = `${trPath}/[slug]`
      if (allSame) {
        result[dynamicKey] = `${trPath}/[slug]`
      } else {
        const dynMap: Record<string, string> = {}
        for (const [loc, path] of Object.entries(filledSegments)) {
          dynMap[loc] = `${path}/[slug]`
        }
        result[dynamicKey] = dynMap
      }
    }
  }

  return result
}

const pathnames = buildPathnames(siteSettings.routeSegments)

// Bilgi Merkezi nested URL (/bilgi-merkezi/<kategori>/<slug>) — buildPathnames
// yalnız 1-seviye /[slug] üretir; nested makale route'u için 2-seviye template
// eklenir ki next-intl Link EN'de /learning/<kat>/<slug>'e çevirsin. Kategori
// landing (/bilgi-merkezi/<kat>) tek-segment olduğu için mevcut /[slug] pattern'i
// zaten doğru prefix'e çevirir (aynı regex); sadece 2-seviye pattern eklenir.
const bmSeg = siteSettings.routeSegments.find((s) => s.key === 'bilgi-merkezi')
if (bmSeg) {
  const trSeg = bmSeg.segments['tr'] ?? '/bilgi-merkezi'
  const nested: Record<string, string> = {}
  for (const loc of SUPPORTED_LOCALES) {
    nested[loc] = `${bmSeg.segments[loc] ?? trSeg}/[kategori]/[slug]`
  }
  pathnames[`${trSeg}/[kategori]/[slug]`] = nested
}

/**
 * next-intl routing config.
 *
 * `locales` = **ENABLED_LOCALES** (sadece yayında olan dil(ler)). Sebep:
 * next-intl middleware bu listeyi okur ve her response'a otomatik HTTP
 * `Link: <url>; rel="alternate"; hreflang="<locale>"` header'ı basar
 * (alternateLinks default açık). Eğer SUPPORTED_LOCALES (tüm 16) verirsek
 * Google'a hayalet diller bildirilir → sitemap.xml + HTML hreflang filter'i
 * boşa gider. ENABLED_LOCALES kullanınca header sadece tr+en içerir.
 *
 * Trade-off: disabled dilde URL açmak (/de/...) 404 verir. Bu istenen davranış —
 * o dilde içerik yoksa zaten "ham i18n key" render edilirdi (404'ten kötü).
 * Yeni dil enable etmek için: siteSettings.locales[].enabled=true + messages/<dil>.json
 * + rebuild. Rebuild zaten i18n key'leri zorunlu kıldığı için locale enable
 * adımı bu sınırlamayı zaten kabul ediyor.
 *
 * SUPPORTED_LOCALES diğer yerlerde (LocaleSwitcher label haritası, admin
 * dil yönetimi) hala kullanılır.
 */
export const routing = defineRouting({
  locales: ENABLED_LOCALES,
  defaultLocale: DEFAULT_LOCALE,
  localePrefix: LOCALE_PREFIX_MODE,
  // alternateLinks KAPALI: next-intl otomatik hreflang Link header'i SADECE segment
  // cevirir (/bilgi-merkezi ↔ /learning), per-locale slug'i degil. Icerik slug'lari
  // dile gore farkli oldugu icin (TR: sql-injection-nedir…, EN: what-is-sql-injection…)
  // otomatik header YANLIS URL uretir → cift/celiskili hreflang. Dogru hreflang'i
  // her sayfa generateMetadata + buildAlternates ile HTML head'de zaten basiyor
  // (per-locale slug ile). next-intl docs: CMS pathname yonetiminde alternateLinks:false.
  alternateLinks: false,
  // next-intl tip imzasi `Pathnames<typeof locales>` bekler; routeSegments runtime
  // veri oldugu icin TS literal narrow olamiyor — cast guvenli (shape next-intl'in
  // bekledigi ile birebir uyumlu).
  pathnames: pathnames as Parameters<typeof defineRouting>[0]['pathnames'],
})

const navigation = createNavigation(routing)

// ─── Dinamik template matcher ──────────────────────────────────────────
//
// pathnames icindeki '/[param]' iceren template'ler regex'e cevrilir.
// Link/router string href'i '/hizmetler/sizma-testi' aldiginda, bu liste
// uzerinden eslestirilip '/hizmetler/[slug]' template + { slug: 'sizma-testi' }
// params'a cevrilir. Sonra IntlLink/IntlRouter object form ile pathnames
// mapping'ini dogru uygular (EN'de /services/sizma-testi).

interface DynamicTemplate {
  /** Orijinal template — '/hizmetler/[slug]' gibi. IntlLink href.pathname'i. */
  readonly template: string
  /** Sirali param adlari. */
  readonly paramNames: readonly string[]
  /** Path matcher regex. */
  readonly regex: RegExp
}

const dynamicTemplates: readonly DynamicTemplate[] = Object.keys(pathnames)
  .filter((t) => t.includes('['))
  .map((template) => {
    const paramNames: string[] = []
    const regexSrc = template.replace(/\[([^\]]+)\]/g, (_, name: string) => {
      paramNames.push(name)
      return '([^/]+)'
    })
    return {
      template,
      paramNames,
      regex: new RegExp(`^${regexSrc}$`),
    }
  })

/**
 * String href'i pathnames dinamik template ile match ettir. Match ederse
 * IntlLink/IntlRouter object form'a cevirir; yoksa null doner.
 */
function matchDynamicHref(
  href: string,
): { pathname: string; params: Record<string, string> } | null {
  // Query/hash strip et — sadece path matching yap, sonra geri ekle
  const [pathOnly] = href.split(/[?#]/)
  if (!pathOnly) return null
  for (const { template, paramNames, regex } of dynamicTemplates) {
    const match = pathOnly.match(regex)
    if (!match) continue
    const params: Record<string, string> = {}
    paramNames.forEach((name, i) => {
      const value = match[i + 1]
      if (value !== undefined) params[name] = value
    })
    return { pathname: template, params }
  }
  return null
}

// ─── Public exports ─────────────────────────────────────────────────────

type IntlLinkProps = Parameters<typeof navigation.Link>[0]
type LinkRest = Omit<IntlLinkProps, 'href'>

/**
 * Locale-aware Link — string href kabul eder, dinamik template'e match
 * ederse IntlLink'e object form ile geçirir (pathnames mapping etkili olur).
 *
 * Kullanim:
 *   <Link href="/hizmetler">...</Link>
 *   <Link href="/hizmetler/sizma-testi">...</Link>
 *   <Link href="/iletisim">...</Link>
 */
export function Link({ href, ...rest }: { href: string } & LinkRest) {
  const IntlLink = navigation.Link
  const dynamic = matchDynamicHref(href)
  if (dynamic) {
    return <IntlLink href={dynamic as IntlLinkProps['href']} {...rest} />
  }
  return <IntlLink href={href as IntlLinkProps['href']} {...rest} />
}

/**
 * Locale-aware router — pathnames'a uymayan dinamik path'leri kabul eder.
 * push/replace string href'i dinamik template'e match ettirir, edip object form'a cevirir.
 */
export function useRouter() {
  const router = navigation.useRouter()

  function toHref(href: string): Parameters<typeof router.push>[0] {
    const dynamic = matchDynamicHref(href)
    if (dynamic) return dynamic as Parameters<typeof router.push>[0]
    return href as Parameters<typeof router.push>[0]
  }

  // next-intl router locale parametresini dar union bekler; siteSettings runtime
  // registry'sinden gelen Locale string'i guvenli kabul edilebilir (middleware
  // zaten bilinmeyen locale'i 404'e yonlendirir).
  type RouterPushOptions = Parameters<typeof router.push>[1]

  return {
    push: (href: string, options?: { locale?: Locale }) =>
      router.push(toHref(href), options as RouterPushOptions),
    replace: (href: string, options?: { locale?: Locale }) =>
      router.replace(toHref(href), options as RouterPushOptions),
    back: () => router.back(),
    forward: () => router.forward(),
    refresh: () => router.refresh(),
    prefetch: (href: string) => router.prefetch(toHref(href) as Parameters<typeof router.prefetch>[0]),
  }
}

export const { redirect, usePathname, getPathname } = navigation
